import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, X, Activity, CheckCircle, ChevronDown } from 'lucide-react';
import WorkflowService from '../../services/workflow.service';
import ConfirmDialog from '../ui/ConfirmDialog';
import useToast from '../../hooks/useToast';
import ToastContainer from '../ui/ToastContainer';

export default function DeploymentStrategy({ workflowId, projectId, accountId, onClose, onStrategyChange }) {
  const { toasts, removeToast, success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingStrategy, setLoadingStrategy] = useState(true);
  const [currentStrategy, setCurrentStrategy] = useState(null);
  const [versions, setVersions] = useState([]);
  const [publishedVersionNumber, setPublishedVersionNumber] = useState(null);
  const [stats, setStats] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmPromote, setConfirmPromote] = useState(false);

  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);
  const versionDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (versionDropdownRef.current && !versionDropdownRef.current.contains(e.target)) {
        setVersionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [formData, setFormData] = useState({
    type: 'canary',
    candidate_version_id: '',
    sampling_rate: 10,
    traffic_percentage: 10,
    auto_rollback_threshold: 20
  });

  useEffect(() => {
    if (workflowId) {
      loadStrategy();
      loadVersions();
    }
  }, [workflowId]);

  useEffect(() => {
    if (currentStrategy) {
      loadStats();
      const interval = setInterval(loadStats, 30000);
      return () => clearInterval(interval);
    }
  }, [currentStrategy]);

  const loadStrategy = async () => {
    setLoadingStrategy(true);
    try {
      const result = await WorkflowService.getDeploymentStrategy(workflowId, projectId);

      if (result.success) {
        const strategy = result.data.strategy;
        setCurrentStrategy(strategy);

        if (strategy) {
          setFormData({
            type: strategy.type,
            candidate_version_id: strategy.candidate_version_id,
            sampling_rate: strategy.sampling_rate || 10,
            traffic_percentage: strategy.traffic_percentage || 10,
            auto_rollback_threshold: strategy.auto_rollback_threshold || 20
          });
        }
      }
    } catch (err) {
      console.error('Error loading strategy:', err);
    } finally {
      setLoadingStrategy(false);
    }
  };

  const loadVersions = async () => {
    try {
      const result = await WorkflowService.listWorkflowVersions(workflowId);

      if (result.success) {
        setVersions(result.data.versions || []);
        setPublishedVersionNumber(result.data.current_published_version_number ?? null);
      }
    } catch (err) {
      console.error('Error loading versions:', err);
    }
  };

  const loadStats = async () => {
    if (!currentStrategy) return;

    try {
      const result = await WorkflowService.getDeploymentStats(workflowId, projectId, 24);
      
      if (result.success) {
        setStats(result.data.stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleConfigure = async () => {
    if (!formData.candidate_version_id) {
      toastError('Version required', 'Please select a candidate version before configuring.');
      return;
    }

    const selectedVersion = versions.find(v => v.id === formData.candidate_version_id);
    if (selectedVersion && publishedVersionNumber != null && selectedVersion.version_number === publishedVersionNumber) {
      toastError('Invalid version', 'The candidate version cannot be the same as the currently published version. Select a different one.');
      return;
    }

    setLoading(true);

    try {
      const strategy = {
        type: formData.type,
        candidate_version_id: formData.candidate_version_id,
        auto_rollback_threshold: formData.auto_rollback_threshold
      };

      if (formData.type === 'shadow') {
        strategy.sampling_rate = formData.sampling_rate;
      } else if (formData.type === 'canary') {
        strategy.percentage = formData.traffic_percentage;
      }

      const result = await WorkflowService.configureDeploymentStrategy(workflowId, {
        account_id: accountId,
        project_id: projectId,
        strategy
      });

      if (result.success) {
        toastSuccess('Strategy configured', `${formData.type.charAt(0).toUpperCase() + formData.type.slice(1)} deployment strategy is now active.`);
        await loadStrategy();
      } else {
        toastError('Configuration failed', result.error || 'Failed to configure strategy');
      }
    } catch (err) {
      toastError('Configuration failed', err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setConfirmDelete(true);
  };

  const executeDelete = async () => {
    setConfirmDelete(false);
    setLoading(true);

    try {
      const result = await WorkflowService.deleteDeploymentStrategy(workflowId, projectId);

      if (result.success) {
        toastSuccess('Strategy removed', 'Deployment strategy cleared. New executions will use the published version.');
        setCurrentStrategy(null);
        setStats(null);
        setTimeout(() => { if (onStrategyChange) onStrategyChange(null); }, 2200);
      } else {
        toastError('Remove failed', result.error || 'Failed to remove deployment strategy');
      }
    } catch (err) {
      toastError('Remove failed', err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = () => {
    setConfirmPromote(true);
  };

  const executePromote = async () => {
    setConfirmPromote(false);
    setPromoting(true);

    try {
      const result = await WorkflowService.promoteCandidate(workflowId, {
        account_id: accountId,
        project_id: projectId
      });

      if (result.success) {
        toastSuccess('Candidate promoted', 'The candidate version is now the new published version.');
        setCurrentStrategy(null);
        setStats(null);
        setTimeout(() => { if (onStrategyChange) onStrategyChange(result.data.workflow); }, 2200);
      } else {
        toastError('Promotion failed', result.error || 'Failed to promote candidate version');
      }
    } catch (err) {
      toastError('Promotion failed', err.message || 'An unexpected error occurred');
    } finally {
      setPromoting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-900 border border-white/10 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Deployment Strategy</h2>
              <p className="text-xs text-gray-400">Configure shadow or canary deployments</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loadingStrategy ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-white/20 border-t-emerald-400 rounded-full animate-spin" />
            </div>
          ) : currentStrategy ? (
            <div className="space-y-6">
              {/* Current Strategy Info */}
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-sm font-semibold text-white">Active Strategy</h3>
                  </div>
                  <span className="px-3 py-1 text-xs font-medium bg-emerald-500/20 text-emerald-300 rounded-full">
                    {currentStrategy.type.toUpperCase()}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-xs text-gray-400">Type</label>
                    <p className="text-white font-medium">{currentStrategy.type}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Auto-Rollback</label>
                    <p className="text-white font-medium">{currentStrategy.auto_rollback_threshold}% error rate</p>
                  </div>
                  {currentStrategy.type === 'shadow' && (
                    <div>
                      <label className="text-xs text-gray-400">Sampling Rate</label>
                      <p className="text-white font-medium">{currentStrategy.sampling_rate}%</p>
                    </div>
                  )}
                  {currentStrategy.type === 'canary' && (
                    <div>
                      <label className="text-xs text-gray-400">Traffic Split</label>
                      <p className="text-white font-medium">{currentStrategy.traffic_percentage}% to candidate</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handlePromote}
                    disabled={promoting}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg transition-all"
                  >
                    {promoting ? 'Promoting...' : 'Promote Candidate'}
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-red-400 border border-red-500/30 hover:bg-red-500/10 disabled:opacity-50 rounded-lg transition-all"
                  >
                    Remove Strategy
                  </button>
                </div>
              </div>

              {/* Stats */}
              {stats && (
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Performance Comparison (Last 24h)
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                      <label className="text-xs text-gray-400">Stable Version</label>
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Executions</span>
                          <span className="text-white font-medium">{stats.stable?.executions || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Success Rate</span>
                          <span className="text-white font-medium">{stats.stable?.success_rate || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Avg Duration</span>
                          <span className="text-white font-medium">{stats.stable?.avg_duration || 0}s</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                      <label className="text-xs text-gray-400">Candidate Version</label>
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Executions</span>
                          <span className="text-white font-medium">{stats.candidate?.executions || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Success Rate</span>
                          <span className="text-white font-medium">{stats.candidate?.success_rate || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Avg Duration</span>
                          <span className="text-white font-medium">{stats.candidate?.avg_duration || 0}s</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Strategy Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Deployment Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData({ ...formData, type: 'shadow' })}
                    className={`p-4 rounded-lg border transition-all ${
                      formData.type === 'shadow'
                        ? 'bg-violet-500/10 border-violet-500/30 ring-2 ring-violet-500/20'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <h4 className="text-sm font-semibold text-white mb-1">Shadow</h4>
                    <p className="text-xs text-gray-400">Run candidate alongside stable, compare results without affecting users</p>
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, type: 'canary' })}
                    className={`p-4 rounded-lg border transition-all ${
                      formData.type === 'canary'
                        ? 'bg-violet-500/10 border-violet-500/30 ring-2 ring-violet-500/20'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <h4 className="text-sm font-semibold text-white mb-1">Canary</h4>
                    <p className="text-xs text-gray-400">Route percentage of traffic to candidate version</p>
                  </button>
                </div>
              </div>

              {/* Candidate Version */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Candidate Version
                </label>
                <div className="relative" ref={versionDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setVersionDropdownOpen((o) => !o)}
                    className={`w-full px-4 py-2.5 bg-white/5 border rounded-lg text-sm text-left flex items-center justify-between transition-all focus:outline-none ${
                      versionDropdownOpen
                        ? 'border-violet-500/50 ring-2 ring-violet-500/20'
                        : 'border-white/10 hover:border-white/20'
                    } ${formData.candidate_version_id ? 'text-white' : 'text-gray-500'}`}
                  >
                    <span>
                      {formData.candidate_version_id
                        ? (() => {
                            const v = versions.find((v) => v.id === formData.candidate_version_id);
                            return v ? `Version ${v.version_number} — ${new Date(v.published_at).toLocaleDateString()}` : 'Select a version...';
                          })()
                        : 'Select a version...'}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${versionDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {versionDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        className="absolute z-50 mt-1 w-full bg-zinc-900 border border-white/15 rounded-lg shadow-xl overflow-hidden"
                      >
                        {versions.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">No versions available</div>
                        ) : (
                          versions.map((version) => {
                            const isPublished = publishedVersionNumber != null && version.version_number === publishedVersionNumber;
                            return (
                              <button
                                key={version.id}
                                type="button"
                                disabled={isPublished}
                                onClick={() => {
                                  if (isPublished) return;
                                  setFormData({ ...formData, candidate_version_id: version.id });
                                  setVersionDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                                  isPublished
                                    ? 'opacity-40 cursor-not-allowed text-gray-400'
                                    : formData.candidate_version_id === version.id
                                    ? 'bg-violet-500/20 text-violet-300'
                                    : 'text-gray-200 hover:bg-white/[0.07]'
                                }`}
                              >
                                <span>
                                  <span className="font-medium">Version {version.version_number}</span>
                                  <span className="text-gray-500 ml-2 text-xs">
                                    {new Date(version.published_at).toLocaleDateString()}
                                  </span>
                                </span>
                                {isPublished && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 ml-2 shrink-0">
                                    published
                                  </span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Sampling Rate (Shadow only) */}
              {formData.type === 'shadow' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sampling Rate: {formData.sampling_rate}%
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={formData.sampling_rate}
                    onChange={(e) => setFormData({ ...formData, sampling_rate: parseInt(e.target.value) })}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-violet-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Percentage of requests to run through shadow deployment
                  </p>
                </div>
              )}

              {/* Traffic Percentage (Canary only) */}
              {formData.type === 'canary' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Traffic Split: {formData.traffic_percentage}%
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={formData.traffic_percentage}
                    onChange={(e) => setFormData({ ...formData, traffic_percentage: parseInt(e.target.value) })}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-violet-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Percentage of traffic to route to candidate version
                  </p>
                </div>
              )}

              {/* Auto-Rollback Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Auto-Rollback Threshold: {formData.auto_rollback_threshold}%
                </label>
                <input
                  type="range"
                  min="5"
                  max="50"
                  value={formData.auto_rollback_threshold}
                  onChange={(e) => setFormData({ ...formData, auto_rollback_threshold: parseInt(e.target.value) })}
                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-red-500"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Automatically rollback if error rate exceeds this threshold
                </p>
              </div>

              {/* Configure Button */}
              <button
                onClick={handleConfigure}
                disabled={loading || !formData.candidate_version_id}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-primary hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all"
              >
                {loading ? 'Configuring...' : 'Configure Strategy'}
              </button>
            </div>
          )}
        </div>
      </motion.div>

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Remove Deployment Strategy"
        message="Remove deployment strategy? New executions will use the published version."
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        onConfirm={executeDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmDialog
        isOpen={confirmPromote}
        title="Promote Candidate"
        message="Promote candidate to published? This will make the candidate the new published version and clear the strategy."
        confirmText="Promote"
        cancelText="Cancel"
        variant="warning"
        onConfirm={executePromote}
        onCancel={() => setConfirmPromote(false)}
      />

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </motion.div>
  );
}
